/**
 * Browser-safe wrapper around `crypto.randomUUID()`.
 *
 * Some browsers (notably Opera with its built-in ad blocker / VPN / Aria AI)
 * and certain browser extensions monkey-patch the `crypto` object or its
 * methods. When `crypto.randomUUID` is reassigned without preserving the
 * correct `this` binding, calling it throws `TypeError: Illegal invocation`.
 *
 * This wrapper:
 * 1. Calls `crypto.randomUUID()` with proper `this` context via `.call()`.
 * 2. Falls back to a properly-formatted UUID v4 string generated via
 *    `crypto.getRandomValues` or `Math.random` if crypto is unavailable.
 */
export function safeRandomUUID() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID.call(crypto);
        }
    }
    catch {
        // Swallow — fall through to the fallback below.
    }
    return uuidV4Fallback();
}
/**
 * Generates a valid UUID v4 string without relying on `crypto.randomUUID()`.
 * Uses `crypto.getRandomValues` when available (still cryptographically strong),
 * otherwise falls back to `Math.random` (not crypto-secure, but structurally valid).
 */
function uuidV4Fallback() {
    const bytes = new Uint8Array(16);
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
            crypto.getRandomValues.call(crypto, bytes);
        }
        else {
            for (let i = 0; i < 16; i++) {
                bytes[i] = (Math.random() * 256) >>> 0;
            }
        }
    }
    catch {
        for (let i = 0; i < 16; i++) {
            bytes[i] = (Math.random() * 256) >>> 0;
        }
    }
    // Set version 4 (bits 4-7 of byte 6)
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    // Set variant 1 (bits 6-7 of byte 8)
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}
//# sourceMappingURL=safeRandomUUID.js.map